import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateRangeInput } from 'components/common/form/date/range';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import useTrainingPerformanceStore, {
  TRAINING_LOG_LIMIT_OPTIONS,
} from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { endOfDay, endOfToday, parseISO, startOfDay } from 'date-fns';
import { PseudoTrainingOutcome } from 'enums/training.enums';
import { t } from 'i18next';
import { LOCAL_DATEPICKER_FORMAT } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { PITCH_TYPE_OPTIONS, PitchType } from 'lib_ts/enums/pitches.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { useEffect, useMemo } from 'react';

const PSEUDO_OUTCOME_OPTIONS: IOption[] = [
  { label: 'Complete', value: PseudoTrainingOutcome.Complete },
  { label: 'Give Up', value: PseudoTrainingOutcome.GiveUp },
  { label: 'PT Failure', value: PseudoTrainingOutcome.PT_Failure },
];

interface IProps {
  machine: IAdminMachine;
}

export const TrainingPerfControls = (props: IProps) => {
  const loading = useTrainingPerformanceStore((s) => s.loading);
  const filters = useTrainingPerformanceStore((s) => s.filters);

  useEffect(() => {
    useTrainingPerformanceStore
      .getState()
      .initialize(props.machine.machineID, props.machine.ball_type);
  }, [props.machine.machineID, props.machine.ball_type]);

  const { period } = filters;

  const ballTypeOptions: IOption[] = useMemo(
    () => props.machine.ball_type_options.map((o) => ({ label: o, value: o })),
    [props.machine.ball_type_options]
  );

  return (
    <ErrorBoundary identifier="TrainingPerfControls">
      <CommonFormGrid columns={5}>
        {/* row 1 */}
        <CommonDateRangeInput
          id="period"
          displayFormat={LOCAL_DATEPICKER_FORMAT}
          defaultStartValue={period.start ? parseISO(period.start) : undefined}
          defaultEndValue={period.end ? parseISO(period.end) : undefined}
          maxDate={endOfToday()}
          onChange={(values) => {
            if (!values || !values.start || !values.end) {
              NotifyHelper.warning({
                message_md: t('common.check-inputs-msg'),
              });
              return;
            }

            const currStart = startOfDay(values.start);
            const currEnd = endOfDay(values.end);

            useTrainingPerformanceStore.getState().mergeFilters({
              period: {
                start: currStart.toISOString(),
                end: currEnd.toISOString(),
              },
            });
          }}
          disabled={loading}
          onChangeOnClose
        />

        <CommonSelectInput
          id="limit"
          value={filters.limit.toString()}
          options={TRAINING_LOG_LIMIT_OPTIONS}
          onNumericChange={(v) =>
            useTrainingPerformanceStore.getState().mergeFilters({
              limit: v,
            })
          }
          disabled={loading}
          skipSort
        />

        <CommonSelectInput
          id="ball_type"
          value={filters.ball_type ?? undefined}
          options={ballTypeOptions}
          onChange={(v) =>
            useTrainingPerformanceStore.getState().mergeFilters({
              ball_type: !v ? null : (v as BallType),
            })
          }
          disabled={loading}
          optional
        />

        <CommonSearchInput
          id="pitchType"
          values={filters.pitchTypes}
          options={PITCH_TYPE_OPTIONS}
          onChange={(v) =>
            useTrainingPerformanceStore.getState().mergeFilters({
              pitchTypes: v as PitchType[],
            })
          }
          placeholder="common.pitch-type"
          disabled={loading}
          optional
        />

        <CommonSelectInput
          id="pseudoOutcome"
          value={filters.pseudoOutcome}
          options={PSEUDO_OUTCOME_OPTIONS}
          onChange={(v) =>
            useTrainingPerformanceStore.getState().mergeFilters({
              pseudoOutcome: v as PseudoTrainingOutcome,
            })
          }
          disabled={loading}
          optional
        />
      </CommonFormGrid>
    </ErrorBoundary>
  );
};
